import type {
  CreateJourneyRequest,
  FlowPatientCalendar,
  JourneyContent,
  JourneyEditorView,
  JourneySummary,
  UpsertJourneyDraft,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// TES-224 — journey authoring client, mirroring flows-api.ts. Auth rides on the
// httpOnly clinician session cookie, scoped by tenantHeader().

function commonHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...tenantHeader() };
}

export class JourneyApiError extends Error {}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) {
    return jsonOr401<T>(res);
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown };
      const msg = body.message ?? body.error;
      if (typeof msg === "string" && msg.trim()) detail = msg;
      else if (Array.isArray(msg) && msg.length) detail = msg.join("; ");
    } catch {
      // non-JSON body — keep the status-code detail
    }
    throw new JourneyApiError(detail);
  }
  return (await res.json()) as T;
}

export async function listJourneys(): Promise<JourneySummary[]> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/journeys`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function fetchJourney(key: string): Promise<JourneyEditorView> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/journeys/${key}`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function createJourney(
  body: CreateJourneyRequest,
): Promise<JourneyContent> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/journeys`, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function saveJourneyDraft(
  key: string,
  body: UpsertJourneyDraft,
): Promise<JourneyContent> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/journeys/${key}/draft`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOrError(res);
}

export async function publishJourney(key: string): Promise<JourneyContent> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/journeys/${key}/publish`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOrError(res);
}

export async function archiveJourney(key: string): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/journeys/${key}/archive`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  if (res.status === 401 || res.status === 403) {
    await jsonOr401<void>(res);
    return;
  }
  if (!res.ok) throw new JourneyApiError(`HTTP ${res.status}`);
}

// --- per-patient: calendar + attach/detach ---------------------------------

export async function fetchPatientCalendar(
  patientId: string,
): Promise<FlowPatientCalendar> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/journeys`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOrError(res);
}

export async function attachJourneyToPatient(
  patientId: string,
  journeyKey: string,
): Promise<{ id: string; created: boolean }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/journeys`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify({ journeyKey }),
      ...withCredentials,
    },
  );
  return jsonOrError(res);
}

export async function detachPatientJourney(
  patientId: string,
  journeyAssignmentId: string,
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/journeys/${journeyAssignmentId}`,
    { method: "DELETE", headers: commonHeaders(), ...withCredentials },
  );
  if (res.status === 401 || res.status === 403) {
    await jsonOr401<void>(res);
    return;
  }
  if (!res.ok) throw new JourneyApiError(`HTTP ${res.status}`);
}
